"use client";

import Image from "next/image";
import Link from "next/link";
import { useMemo, useState } from "react";
import { BottomNav } from "../_components/bottom-nav";
import { ExploreFooter } from "../_components/explore-footer";
import { buildExploreToolHref } from "../_lib/explore-detail-hrefs";
import { useExploreRoute } from "../_lib/explore-route-context";
import type { AllProductsItem, AllProductsPageData } from "../types";

const ENTITY_TYPE_SECTIONS: Array<{
  entityType: AllProductsItem["entityType"];
  title: string;
  emoji: string;
}> = [
  { entityType: "SOFTWARE", title: "Software & Services", emoji: "🛠️" },
  { entityType: "MODEL_FAMILY", title: "Model Families", emoji: "🧠" },
  { entityType: "MODEL_RELEASE", title: "Model Releases", emoji: "🚀" },
];

export function AllToolsPage({ data }: { data: AllProductsPageData }) {
  const { products, totalCount } = data;
  const { exploreBase } = useExploreRoute();

  const [searchQuery, setSearchQuery] = useState("");

  const filteredProducts = useMemo(() => {
    if (!searchQuery) {
      return products;
    }
    const q = searchQuery.toLowerCase();
    return products.filter(
      (p) =>
        p.displayName.toLowerCase().includes(q) ||
        p.tagline?.toLowerCase().includes(q) ||
        p.companyName?.toLowerCase().includes(q)
    );
  }, [products, searchQuery]);

  const productsByEntityType = useMemo(() => {
    const grouped = new Map<string, AllProductsItem[]>();
    for (const product of filteredProducts) {
      const existing = grouped.get(product.entityType) ?? [];
      existing.push(product);
      grouped.set(product.entityType, existing);
    }
    return grouped;
  }, [filteredProducts]);

  return (
    <main
      className="pb-[calc(var(--explore-bottom-nav-h)+var(--explore-safe-bottom)+16px)] lg:pb-0"
      id="main-content"
      tabIndex={-1}
    >
      <div className="mx-auto w-full max-w-[1320px] px-4 sm:px-6">
        {/* Hero */}
        <div className="py-6 pb-4">
          <h1
            className="mb-1.5 font-bold text-[var(--explore-text-primary)]"
            style={{
              fontFamily: "var(--explore-card-title-stack)",
              fontSize: "32px",
              lineHeight: "1.2",
              fontWeight: 700,
            }}
          >
            AI Products & Tools
          </h1>
          <p className="max-w-[600px] text-[1rem] text-[var(--explore-text-secondary)]">
            Discover the best AI tools, models, and services. Curated and
            tracked by the AI Primer team.
          </p>
        </div>

        {/* Stats */}
        <div className="flex flex-wrap items-center gap-8 pb-6">
          <div className="flex items-baseline gap-1.5">
            <span className="font-bold text-[1.5rem] text-[var(--explore-text-primary)]">
              {totalCount}
            </span>
            <span className="text-[0.9rem] text-[var(--explore-text-muted)]">
              products
            </span>
          </div>
          <Link
            className="ml-auto text-[0.85rem] text-[var(--explore-accent-teal)] hover:underline"
            href={`${exploreBase}/tools/categories`}
          >
            Browse by category →
          </Link>
        </div>

        {/* Search */}
        <div className="pb-6">
          <div className="relative max-w-[350px]">
            <svg
              className="-translate-y-1/2 absolute top-1/2 left-3.5 h-4 w-4 text-[var(--explore-text-muted)]"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              viewBox="0 0 24 24"
            >
              <circle cx="11" cy="11" r="8" />
              <path d="m21 21-4.35-4.35" />
            </svg>
            <input
              className="w-full rounded-full border border-[var(--explore-border-light)] bg-[var(--explore-bg-card)] py-2 pr-4 pl-10 text-[0.85rem] text-[var(--explore-text-secondary)] outline-none transition-all focus:border-[var(--explore-accent-teal)] focus:ring-[3px] focus:ring-[var(--explore-focus-ring)]"
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search products..."
              type="text"
              value={searchQuery}
            />
          </div>
        </div>

        {/* Product Sections */}
        <div className="pb-12">
          {ENTITY_TYPE_SECTIONS.map((section) => {
            const sectionProducts =
              productsByEntityType.get(section.entityType) ?? [];
            if (sectionProducts.length === 0) {
              return null;
            }

            return (
              <section className="mb-10" key={section.entityType}>
                <div className="mb-4 flex items-center gap-3 border-[var(--explore-border-light)] border-b pb-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-[10px] bg-[var(--explore-bg-highlight)] text-[1.25rem]">
                    {section.emoji}
                  </div>
                  <h3 className="font-bold text-[1.25rem] text-[var(--explore-text-primary)]">
                    {section.title}
                  </h3>
                  <span className="ml-auto text-[0.8rem] text-[var(--explore-text-muted)]">
                    {sectionProducts.length} products
                  </span>
                </div>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                  {sectionProducts.map((product) => (
                    <ProductCard
                      exploreBase={exploreBase}
                      key={product.slug}
                      product={product}
                    />
                  ))}
                </div>
              </section>
            );
          })}

          {filteredProducts.length === 0 && (
            <div className="py-12 text-center text-[var(--explore-text-muted)]">
              No products match your search.
            </div>
          )}
        </div>
      </div>

      <ExploreFooter />
      <BottomNav />
    </main>
  );
}

function ProductCard({
  product,
  exploreBase,
}: {
  product: AllProductsItem;
  exploreBase: string;
}) {
  return (
    <Link
      className="hover:-translate-y-0.5 relative flex flex-col rounded-xl border border-[var(--explore-border-light)] bg-[var(--explore-bg-card)] p-4 no-underline shadow-[var(--explore-inline-shadow)] transition-all hover:border-[var(--explore-border)] hover:shadow-[var(--explore-shadow-hover)]"
      href={buildExploreToolHref({
        exploreBase,
        productSlug: product.slug,
      })}
    >
      <div className="mb-3 flex items-center gap-3">
        <div className="flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden rounded-[10px] bg-[var(--explore-bg-highlight)]">
          {product.iconUrl ? (
            <Image
              alt={product.displayName}
              className="h-full w-full rounded-[10px] object-cover"
              height={44}
              src={product.iconUrl}
              width={44}
            />
          ) : (
            <span className="text-[1.4rem]">
              {product.emoji ?? product.displayName.charAt(0)}
            </span>
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-bold text-[0.95rem] text-[var(--explore-text-primary)]">
            {product.displayName}
          </div>
          {product.companyName && (
            <div className="text-[0.7rem] text-[var(--explore-text-muted)]">
              {product.companyName}
            </div>
          )}
        </div>
      </div>
      {product.tagline && (
        <div className="line-clamp-2 text-[0.8rem] text-[var(--explore-text-secondary)] leading-[1.45]">
          {product.tagline}
        </div>
      )}
      <div className="mt-auto pt-2 text-[0.75rem] text-[var(--explore-text-muted)]">
        {product.storyCount} {product.storyCount === 1 ? "story" : "stories"}
      </div>
    </Link>
  );
}
